import { useState, useEffect, useCallback } from 'react';
import { TopBar } from '../components/layout/TopBar';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import {
  Building2, Plus, Users, Calendar, DollarSign, Phone, Mail, Globe,
  Search, Edit, Trash2, CheckCircle, Clock, XCircle, FileText, RefreshCw
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';
const authHeader = () => ({ 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('token')}` });

const STATUS_COLORS = {
  active: 'bg-emerald-100 text-emerald-700',
  inactive: 'bg-gray-100 text-gray-600',
  pending: 'bg-amber-100 text-amber-700',
  confirmed: 'bg-blue-100 text-blue-700',
  inquiry: 'bg-purple-100 text-purple-700',
  cancelled: 'bg-red-100 text-red-700',
};
const STATUS_LABEL = { active: 'Activo', inactive: 'Inactivo', pending: 'Pendiente', confirmed: 'Confirmado', inquiry: 'Consulta', cancelled: 'Cancelado' };

const EMPTY_ACCOUNT = { name: '', contact_name: '', contact_email: '', contact_phone: '', company_type: 'empresa', notes: '', credit_limit: 0, discount_pct: 0 };
const EMPTY_BOOKING = { account_id: '', account_name: '', event_name: '', event_date: '', guests: 2, room_preference: '', menu_notes: '', budget: '', contact_email: '', contact_phone: '' };

export default function CorporatePortal() {
  const [accounts, setAccounts] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [showAccountModal, setShowAccountModal] = useState(false);
  const [showBookingModal, setShowBookingModal] = useState(false);
  const [editAccount, setEditAccount] = useState(null);
  const [editBooking, setEditBooking] = useState(null);
  const [form, setForm] = useState(EMPTY_ACCOUNT);
  const [bookingForm, setBookingForm] = useState(EMPTY_BOOKING);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [accsRes, bksRes] = await Promise.all([
        fetch(`${API_URL}/api/corporate/accounts`, { headers: authHeader() }),
        fetch(`${API_URL}/api/corporate/bookings`, { headers: authHeader() }),
      ]);
      if (accsRes.ok) setAccounts(await accsRes.json());
      if (bksRes.ok) setBookings(await bksRes.json());
    } catch { toast.error('Error al cargar datos'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openNewAccount = () => { setEditAccount(null); setForm(EMPTY_ACCOUNT); setShowAccountModal(true); };
  const openEditAccount = (a) => { setEditAccount(a); setForm(a); setShowAccountModal(true); };
  const openNewBooking = (acc = null) => {
    setEditBooking(null);
    setBookingForm(acc ? { ...EMPTY_BOOKING, account_id: acc.id, account_name: acc.name, contact_email: acc.contact_email, contact_phone: acc.contact_phone } : EMPTY_BOOKING);
    setShowBookingModal(true);
  };

  const saveAccount = async () => {
    setSaving(true);
    try {
      const method = editAccount ? 'PUT' : 'POST';
      const url = editAccount ? `${API_URL}/api/corporate/accounts/${editAccount.id}` : `${API_URL}/api/corporate/accounts`;
      const res = await fetch(url, { method, headers: authHeader(), body: JSON.stringify(form) });
      if (res.ok) { toast.success(editAccount ? 'Cuenta actualizada' : 'Cuenta creada'); setShowAccountModal(false); load(); }
      else toast.error('Error al guardar');
    } catch { toast.error('Error de conexión'); }
    finally { setSaving(false); }
  };

  const saveBooking = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${API_URL}/api/corporate/bookings`, { method: 'POST', headers: authHeader(), body: JSON.stringify(bookingForm) });
      if (res.ok) { toast.success('Reservación corporativa creada'); setShowBookingModal(false); load(); }
      else toast.error('Error al guardar');
    } catch { toast.error('Error de conexión'); }
    finally { setSaving(false); }
  };

  const deleteAccount = async (id) => {
    if (!window.confirm('¿Eliminar esta cuenta corporativa?')) return;
    await fetch(`${API_URL}/api/corporate/accounts/${id}`, { method: 'DELETE', headers: authHeader() });
    toast.success('Cuenta eliminada'); load();
  };

  const updateBookingStatus = async (id, status) => {
    await fetch(`${API_URL}/api/corporate/bookings/${id}/status`, { method: 'PUT', headers: authHeader(), body: JSON.stringify({ status }) });
    toast.success('Estado actualizado'); load();
  };

  const filtered = accounts.filter(a => a.name?.toLowerCase().includes(search.toLowerCase()) || a.contact_name?.toLowerCase().includes(search.toLowerCase()));

  const stats = [
    { label: 'Cuentas activas', value: accounts.filter(a => a.status === 'active').length, icon: Building2, color: 'text-blue-500' },
    { label: 'Reservaciones este mes', value: bookings.filter(b => b.created_at?.startsWith(new Date().toISOString().slice(0, 7))).length, icon: Calendar, color: 'text-orange-500' },
    { label: 'Total comensales', value: bookings.reduce((s, b) => s + (Number(b.guests) || 0), 0), icon: Users, color: 'text-purple-500' },
    { label: 'Confirmadas', value: bookings.filter(b => b.status === 'confirmed').length, icon: CheckCircle, color: 'text-emerald-500' },
  ];

  return (
    <div className="flex flex-col h-full overflow-hidden">
      <TopBar title="Portal Corporativo" subtitle="Gestiona cuentas y reservaciones de grupos empresariales" helpSection="corporate" />
      <div className="flex-1 overflow-y-auto p-6 space-y-6">

        {/* Stats */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {stats.map((s, i) => (
            <Card key={i}><CardContent className="p-4 flex items-center gap-3">
              <s.icon className={`w-8 h-8 ${s.color}`} />
              <div><p className="text-2xl font-black">{s.value}</p><p className="text-xs text-gray-500">{s.label}</p></div>
            </CardContent></Card>
          ))}
        </div>

        <Tabs defaultValue="accounts">
          <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
            <TabsList>
              <TabsTrigger value="accounts">Cuentas Corporativas</TabsTrigger>
              <TabsTrigger value="bookings">Reservaciones</TabsTrigger>
            </TabsList>
            <div className="flex gap-2">
              <div className="relative"><Search className="absolute left-3 top-2.5 w-4 h-4 text-gray-400" /><Input placeholder="Buscar..." value={search} onChange={e => setSearch(e.target.value)} className="pl-9 w-48" /></div>
              <Button onClick={openNewAccount} className="bg-orange-500 hover:bg-orange-600 text-white gap-1"><Plus className="w-4 h-4" /> Cuenta</Button>
              <Button onClick={() => openNewBooking()} variant="outline" className="gap-1"><Calendar className="w-4 h-4" /> Reservación</Button>
            </div>
          </div>

          <TabsContent value="accounts">
            {loading ? (
              <div className="flex justify-center py-16"><RefreshCw className="w-8 h-8 text-orange-500 animate-spin" /></div>
            ) : filtered.length === 0 ? (
              <div className="text-center py-16 text-gray-400">
                <Building2 className="w-12 h-12 mx-auto mb-3 opacity-30" />
                <p className="font-medium">No hay cuentas corporativas</p>
                <p className="text-sm">Agrega hoteles, agencias o empresas para gestionar sus reservaciones</p>
                <Button onClick={openNewAccount} className="mt-4 bg-orange-500 hover:bg-orange-600 text-white"><Plus className="w-4 h-4 mr-1" /> Primera cuenta</Button>
              </div>
            ) : (
              <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-4">
                {filtered.map(acc => (
                  <Card key={acc.id} className="hover:shadow-md transition-shadow">
                    <CardContent className="p-5">
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex items-center gap-2">
                          <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
                            <Building2 className="w-5 h-5 text-blue-600" />
                          </div>
                          <div>
                            <h3 className="font-bold text-gray-900">{acc.name}</h3>
                            <p className="text-xs text-gray-500 capitalize">{acc.company_type || 'empresa'}</p>
                          </div>
                        </div>
                        <Badge className={STATUS_COLORS[acc.status] || STATUS_COLORS.active}>{STATUS_LABEL[acc.status] || 'Activo'}</Badge>
                      </div>
                      <div className="space-y-1.5 text-sm text-gray-600 mb-4">
                        {acc.contact_name && <div className="flex items-center gap-2"><Users className="w-3.5 h-3.5" />{acc.contact_name}</div>}
                        {acc.contact_email && <div className="flex items-center gap-2"><Mail className="w-3.5 h-3.5" />{acc.contact_email}</div>}
                        {acc.contact_phone && <div className="flex items-center gap-2"><Phone className="w-3.5 h-3.5" />{acc.contact_phone}</div>}
                        {acc.discount_pct > 0 && <div className="flex items-center gap-2 text-orange-600 font-medium"><DollarSign className="w-3.5 h-3.5" />{acc.discount_pct}% descuento</div>}
                      </div>
                      <div className="flex gap-2">
                        <Button size="sm" variant="outline" onClick={() => openNewBooking(acc)} className="flex-1 gap-1"><Calendar className="w-3.5 h-3.5" /> Reservar</Button>
                        <Button size="sm" variant="ghost" onClick={() => openEditAccount(acc)}><Edit className="w-3.5 h-3.5" /></Button>
                        <Button size="sm" variant="ghost" className="text-red-500" onClick={() => deleteAccount(acc.id)}><Trash2 className="w-3.5 h-3.5" /></Button>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          <TabsContent value="bookings">
            <div className="space-y-3">
              {bookings.length === 0 ? (
                <div className="text-center py-16 text-gray-400">
                  <Calendar className="w-12 h-12 mx-auto mb-3 opacity-30" />
                  <p className="font-medium">No hay reservaciones corporativas</p>
                </div>
              ) : bookings.map(b => (
                <Card key={b.id}><CardContent className="p-4">
                  <div className="flex items-center justify-between flex-wrap gap-3">
                    <div className="flex items-center gap-3">
                      <div className="w-9 h-9 rounded-lg bg-purple-100 flex items-center justify-center"><FileText className="w-4 h-4 text-purple-600" /></div>
                      <div>
                        <h4 className="font-bold text-gray-900">{b.event_name || 'Evento corporativo'}</h4>
                        <p className="text-sm text-gray-500">{b.account_name} · {b.guests} personas · {b.event_date}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge className={STATUS_COLORS[b.status] || STATUS_COLORS.inquiry}>{STATUS_LABEL[b.status] || b.status}</Badge>
                      {b.status === 'inquiry' && (
                        <Button size="sm" className="bg-emerald-500 hover:bg-emerald-600 text-white" onClick={() => updateBookingStatus(b.id, 'confirmed')}>
                          <CheckCircle className="w-3.5 h-3.5 mr-1" /> Confirmar
                        </Button>
                      )}
                      {b.status === 'confirmed' && (
                        <Button size="sm" variant="ghost" className="text-red-500" onClick={() => updateBookingStatus(b.id, 'cancelled')}>
                          <XCircle className="w-3.5 h-3.5" />
                        </Button>
                      )}
                    </div>
                  </div>
                  {b.menu_notes && <p className="text-xs text-gray-500 mt-2 ml-12">📝 {b.menu_notes}</p>}
                </CardContent></Card>
              ))}
            </div>
          </TabsContent>
        </Tabs>
      </div>

      {/* Account Modal */}
      <Dialog open={showAccountModal} onOpenChange={setShowAccountModal}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editAccount ? 'Editar Cuenta' : 'Nueva Cuenta Corporativa'}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2"><Label>Nombre de la empresa *</Label><Input value={form.name || ''} onChange={e => setForm(p => ({ ...p, name: e.target.value }))} placeholder="Hotel Camino Real, Empresa ABC..." /></div>
              <div><Label>Tipo</Label>
                <Select value={form.company_type || 'empresa'} onValueChange={v => setForm(p => ({ ...p, company_type: v }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {['empresa', 'hotel', 'agencia', 'gobierno', 'otro'].map(t => <SelectItem key={t} value={t} className="capitalize">{t}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div><Label>Descuento (%)</Label><Input type="number" min="0" max="50" value={form.discount_pct || 0} onChange={e => setForm(p => ({ ...p, discount_pct: Number(e.target.value) }))} /></div>
              <div><Label>Contacto principal</Label><Input value={form.contact_name || ''} onChange={e => setForm(p => ({ ...p, contact_name: e.target.value }))} placeholder="Nombre del contacto" /></div>
              <div><Label>Teléfono</Label><Input value={form.contact_phone || ''} onChange={e => setForm(p => ({ ...p, contact_phone: e.target.value }))} placeholder="+52 55..." /></div>
              <div className="col-span-2"><Label>Email</Label><Input type="email" value={form.contact_email || ''} onChange={e => setForm(p => ({ ...p, contact_email: e.target.value }))} /></div>
              <div className="col-span-2"><Label>Notas internas</Label><Textarea value={form.notes || ''} onChange={e => setForm(p => ({ ...p, notes: e.target.value }))} rows={2} /></div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAccountModal(false)}>Cancelar</Button>
            <Button onClick={saveAccount} disabled={saving} className="bg-orange-500 hover:bg-orange-600 text-white">
              {saving ? <RefreshCw className="w-4 h-4 animate-spin" /> : 'Guardar'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Booking Modal */}
      <Dialog open={showBookingModal} onOpenChange={setShowBookingModal}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Nueva Reservación Corporativa</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div><Label>Cuenta corporativa</Label>
              <Select value={bookingForm.account_id || ''} onValueChange={v => {
                const acc = accounts.find(a => a.id === v);
                setBookingForm(p => ({ ...p, account_id: v, account_name: acc?.name || '', contact_email: acc?.contact_email || '', contact_phone: acc?.contact_phone || '' }));
              }}>
                <SelectTrigger><SelectValue placeholder="Seleccionar cuenta..." /></SelectTrigger>
                <SelectContent>{accounts.map(a => <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2"><Label>Nombre del evento</Label><Input value={bookingForm.event_name || ''} onChange={e => setBookingForm(p => ({ ...p, event_name: e.target.value }))} placeholder="Cena de navidad, Reunión de directivos..." /></div>
              <div><Label>Fecha</Label><Input type="date" value={bookingForm.event_date || ''} onChange={e => setBookingForm(p => ({ ...p, event_date: e.target.value }))} /></div>
              <div><Label>Número de personas</Label><Input type="number" min="2" value={bookingForm.guests || 2} onChange={e => setBookingForm(p => ({ ...p, guests: Number(e.target.value) }))} /></div>
              <div><Label>Presupuesto estimado (MXN)</Label><Input value={bookingForm.budget || ''} onChange={e => setBookingForm(p => ({ ...p, budget: e.target.value }))} placeholder="$10,000..." /></div>
              <div><Label>Preferencia de sala</Label><Input value={bookingForm.room_preference || ''} onChange={e => setBookingForm(p => ({ ...p, room_preference: e.target.value }))} placeholder="Sala privada, terraza..." /></div>
              <div className="col-span-2"><Label>Notas de menú y alérgenos</Label><Textarea value={bookingForm.menu_notes || ''} onChange={e => setBookingForm(p => ({ ...p, menu_notes: e.target.value }))} rows={2} placeholder="Vegetarianos, alérgenos, platos especiales..." /></div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowBookingModal(false)}>Cancelar</Button>
            <Button onClick={saveBooking} disabled={saving} className="bg-orange-500 hover:bg-orange-600 text-white">
              {saving ? <RefreshCw className="w-4 h-4 animate-spin" /> : 'Crear Reservación'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
